import React, { useEffect, useState, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import useWebSocket from 'react-use-websocket';

import {
  Layout,
  Button,
} from '@lockerpm/design';

import {
  CloseOutlined,
  VerticalAlignTopOutlined,
} from '@ant-design/icons';

import layoutsComponents from './components';
import layoutComponents from '../components/layout';

import global from "../config/global";
import storeActions from "../store/actions";
import commonServices from '../services/common';
import authServices from '../services/auth';
import projectServices from '../secrets-web/src/services/project';

import { scrollToTop } from '../secrets-web/src/utils/common';

import '../secrets-web/src/layouts/css/index.scss'
import './css/index.scss';

function AdminLayout(props) {
  const { PageContent, Onboarding } = layoutComponents;
  const { Header, SidebarTop, SidebarCenter, ImportExportProgress } = layoutsComponents;

  const {
    routers,
    pages,
  } = props

  const accessToken = authServices.access_token();
  const { lastMessage } = useWebSocket(`${process.env.REACT_APP_WS_URL}?token=${accessToken}`);

  const location = useLocation()
  const userInfo = useSelector((state) => state.auth.userInfo)
  const isMobile = useSelector((state) => state.system.isMobile);
  const collapsed = useSelector((state) => state.system.collapsed);
  const currentPage = useSelector((state) => state.system.currentPage);
  const isScrollToTop = useSelector((state) => state.system.isScrollToTop);
  const isFetchProjects = useSelector((state) => state.project.isFetchProjects);
  const selectedProject = useSelector((state) => state.project.selectedProject);
  const selectedWorkspace = useSelector(state => state.workspace.selectedWorkspace);

  const [isLocked, setIsLocked] = useState(false)
  const [respCollapsed, setRespCollapsed] = useState(false);

  useEffect(() => {
    if (lastMessage) {
      global.store.dispatch(storeActions.updateSocketLastMessage(lastMessage))
      const strData = lastMessage?.data.split("'").join('"')
      const messageData = JSON.parse(strData)
      if (messageData.event === 'sync') {
        handleSyncWsData(messageData)
      }
    }
  }, [lastMessage]);

  const hideLayout = useMemo(() => {
    return !!currentPage.hideLayout
  }, [currentPage])

  useEffect(() => {
    global.store.dispatch(storeActions.updateIsScrollToTop(false));
    convertSize()
  }, [location])

  useEffect(() => {
    if (isFetchProjects) {
      fetchProjects()
    }
  }, [isFetchProjects])

  useEffect(() => {
    if (selectedProject) {
      commonServices.sync_project_environments(selectedProject)
      commonServices.sync_project_secrets(selectedProject)
    }
  }, [selectedProject?.id])

  window.addEventListener("resize", (event) => {
    convertSize()
  });

  useEffect(() => {
    if (global.jsCore?.vaultTimeoutService) {
      setInterval(async () => {
        const isLocked = await global.jsCore?.vaultTimeoutService.isLocked();
        setIsLocked(isLocked)
      }, [5 * 1000])
    }
  }, [global.jsCore])

  useEffect(() => {
    checkVaultTimeOut();
  }, [isLocked, userInfo])

  const fetchProjects = async () => {
    await commonServices.sync_profile();
    await projectServices.list({ paging: 0, organization_id: selectedWorkspace?.id }).then((response) => {
      global.store.dispatch(storeActions.updateProjects(response));
      const projectId = currentPage.params?.project_id;
      if (projectId && !!response.find((p) => p.id == projectId)) {
        global.store.dispatch(storeActions.updateSelectedProject(response.find((p) => p.id == projectId)))
      } else {
        global.store.dispatch(storeActions.updateSelectedProject(null))
      }
    }).catch(() => {
      global.store.dispatch(storeActions.updateProjects([]))
      global.store.dispatch(storeActions.updateSelectedProject(null))
    })
    global.store.dispatch(storeActions.updateIsFetchProjects(false));
  }

  const checkVaultTimeOut = async () => {
    const timeoutAction = userInfo?.timeout_action;
    if (isLocked) {
      if (timeoutAction == global.constants.TIMEOUT_ACTION.LOCK) {
        await authServices.redirect_login()
      } else {
        await authServices.logout()
      }
    }
  }

  const handleSyncWsData = async (messageData) => {
    global.store.dispatch(storeActions.updateSyncing(true))
    if (messageData.type.includes('project_member')) {
      fetchProjects();
      if (messageData.type.includes('deleted')) {
        if (selectedProject?.id == messageData?.data.project_id) {
          global.navigate('PROJECTS', { workspace_id: selectedWorkspace?.id })
        }
      }
      if (messageData.type.includes('role_updated')) {
        if (selectedProject?.id == messageData?.data.project_id) {
          global.navigate('PROJECT_SECRETS', { workspace_id: selectedWorkspace?.id, project_id: selectedProject?.id })
        }
      }
    } else if (messageData.type.includes('org_member')) {
      if (messageData.type.includes('deleted')) {
        await commonServices.sync_profile();
        if (selectedWorkspace?.id == messageData?.data?.organization_id) {
          commonServices.sync_data();
        }
      }
    }
    global.store.dispatch(storeActions.updateSyncing(false));
  }

  const convertSize = () => {
    if (window.innerWidth <= 768) {
      global.store.dispatch(storeActions.updateIsMobile(true))
      global.store.dispatch(storeActions.updateCollapsed(false))
    } else if (window.innerWidth <= 1024) {
      global.store.dispatch(storeActions.updateIsMobile(false))
      setRespCollapsed(false)
      if (!collapsed) {
        global.store.dispatch(storeActions.updateCollapsed(true))
      }
    } else if (window.innerWidth <= 1248) {
      setRespCollapsed(false)
      global.store.dispatch(storeActions.updateIsColumn(false))
      global.store.dispatch(storeActions.updateIsMobile(false))
      global.store.dispatch(storeActions.updateCollapsed(false))
    } else {
      setRespCollapsed(false)
      global.store.dispatch(storeActions.updateIsColumn(false))
      global.store.dispatch(storeActions.updateIsMobile(false))
      global.store.dispatch(storeActions.updateCollapsed(false))
    }
  }

  return (
    <>
      {
        hideLayout && <Layout className="admin-no-layout">
          <Layout.Content className="admin-no-layout__content">
            <PageContent routers={routers} pages={pages} />
          </Layout.Content>
        </Layout>
      }
      {
        !hideLayout && <Layout className='admin-layout'>
          {
            !respCollapsed && <Layout.Sider
              trigger={null}
              collapsible
              collapsed={collapsed}
              className={respCollapsed ? '' : 'resp-collapsed'}
            >
              <SidebarTop
                collapsed={collapsed}
              />
              <SidebarCenter
                collapsed={collapsed}
                routers={routers}
              />
              {
                isMobile && <div className='resp-menu-toggle-icon mr-3' onClick={() => setRespCollapsed(true)}>
                  <CloseOutlined />
                </div>
              }
            </Layout.Sider>
          }
          <Layout
            className={`admin-layout-center ${collapsed ? 'admin-layout-center-collapsed' : ''} ${isMobile ? 'mobile' : ''}`}
          >
            <Layout.Header style={{ padding: 0 }}>
              <Header
                className="admin-layout-header"
                collapsed={collapsed}
                setCollapsed={(v) => global.store.dispatch(storeActions.updateCollapsed(v))}
                setRespCollapsed={setRespCollapsed}
              />
            </Layout.Header>
            <Layout.Content
              className='admin-layout-content'
            >
              <Onboarding />
              <PageContent
                routers={routers}
                pages={pages}
              />
            </Layout.Content>
          </Layout>
          {
            isScrollToTop && <div className='admin-layout__button'>
              <Button
                type="primary"
                shape="circle"
                onClick={() => scrollToTop()}
                icon={<VerticalAlignTopOutlined />} size={'large'}
              />
            </div>
          }
          <ImportExportProgress />
        </Layout>
      }
    </>
  )
}

export default AdminLayout;
